import {useDisclosure} from '@mantine/hooks';
import React, {useEffect, useRef} from 'react';

import MergeTransitionModal, {MergeTransitionModalResult, TransitionOption} from '../MergeTransitionModal';

export interface ShowMergeModalParams {
  existingTransitions: Array<TransitionOption>;
  newSourceNodeName: string;
  targetNodeName: string;
}

export type ShowMergeModalFn = (params: ShowMergeModalParams) => Promise<MergeTransitionModalResult | null>;

interface UseMergeTransitionModalReturn {
  showMergeModal: ShowMergeModalFn;
  mergeTransitionModalJsx: JSX.Element;
}

export function useMergeTransitionModal(): UseMergeTransitionModalReturn {
  const [opened, {open, close}] = useDisclosure(false);
  const pendingParamsRef = useRef<ShowMergeModalParams | null>(null);
  const resolveRef = useRef<((result: MergeTransitionModalResult | null) => void) | null>(null);

  useEffect(
    () => () => {
      // Only support one pending merge prompt at a time.
      resolveRef.current?.(null);
      resolveRef.current = null;
      pendingParamsRef.current = null;
    },
    []
  );

  const showMergeModal: ShowMergeModalFn = (params) => {
    resolveRef.current?.(null);
    pendingParamsRef.current = params;
    open();
    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  };

  const handleComplete = (result: MergeTransitionModalResult): void => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    close();
  };

  const handleClose = (): void => {
    resolveRef.current?.(null);
    resolveRef.current = null;
    close();
  };

  const params = pendingParamsRef.current;

  const mergeTransitionModalJsx = (
    <MergeTransitionModal
      opened={opened}
      onComplete={handleComplete}
      onClose={handleClose}
      existingTransitions={params?.existingTransitions ?? []}
      newSourceNodeName={params?.newSourceNodeName ?? ''}
      targetNodeName={params?.targetNodeName ?? ''}
    />
  );

  return {showMergeModal, mergeTransitionModalJsx};
}
